// Communications Analytics — sent / delivered / read / failed across
// WhatsApp, email, voice for a chosen date range. Per-channel KPIs, daily
// activity table, top message kinds.

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { current_language } from '../i18n/index.js';
import { offset_ymd, today_ymd_ist } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';

interface ChannelTotals {
  total: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
}
interface CommsData {
  from_date: string;
  to_date: string;
  totals: { whatsapp: ChannelTotals; email: ChannelTotals; voice: ChannelTotals };
  daily: Array<{
    date: string;
    whatsapp_sent: number;
    whatsapp_delivered: number;
    whatsapp_read: number;
    whatsapp_failed: number;
    email_sent: number;
    email_delivered: number;
    email_opened: number;
    email_failed: number;
    voice_placed: number;
    voice_answered: number;
    voice_failed: number;
  }>;
  top_intents: Array<{ kind: string; count: number; channel: string }>;
}

export function ReportsCommsPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const today = today_ymd_ist();
  const [from_date, set_from_date] = useState(offset_ymd(today, -30));
  const [to_date, set_to_date] = useState(today);

  const q = useQuery({
    queryKey: ['report-comms', from_date, to_date],
    queryFn: () => orpc.reports.comms_analytics({ from_date, to_date }) as Promise<CommsData>,
  });
  const data = q.data;

  const export_csv = (): void => {
    if (!data) return;
    const lines = [
      'date,wa_sent,wa_delivered,wa_read,wa_failed,email_sent,email_delivered,email_opened,email_failed,voice_placed,voice_answered,voice_failed',
      ...data.daily.map((d) =>
        [
          d.date,
          d.whatsapp_sent,
          d.whatsapp_delivered,
          d.whatsapp_read,
          d.whatsapp_failed,
          d.email_sent,
          d.email_delivered,
          d.email_opened,
          d.email_failed,
          d.voice_placed,
          d.voice_answered,
          d.voice_failed,
        ].join(','),
      ),
    ];
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comms-analytics-${from_date}-to-${to_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [preview, set_preview] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () => orpc.reports.comms_analytics_pdf({ from_date, to_date }),
    onSuccess: (out) => {
      if (out.preview_html) set_preview(out.preview_html);
      else window.open(out.pdf_url, '_blank');
    },
  });

  const channel_card = (label_ta: string, label_en: string, totals: ChannelTotals, color_class = ''): ReactElement => {
    const delivery_rate = totals.sent > 0 ? (totals.delivered / totals.sent) * 100 : 0;
    const read_rate = totals.delivered > 0 ? (totals.read / totals.delivered) * 100 : 0;
    return (
      <div className={'kpi ' + color_class}>
        <div className="kpi-label">{t(label_ta, label_en)}</div>
        <div className="kpi-value">{totals.sent}</div>
        <div className="kpi-sub">
          {totals.delivered} {t('வழங்கப்பட்டது', 'delivered')} ({delivery_rate.toFixed(0)}%) ·{' '}
          {read_rate.toFixed(0)}% {t('படிக்கப்பட்டது', 'read')} ·{' '}
          <span style={{ color: totals.failed > 0 ? 'var(--bad)' : 'inherit' }}>{totals.failed} {t('தோல்வி', 'failed')}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to="/reports" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12 }}>
              ← {t('அறிக்கைகள்', 'Reports')}
            </Link>
          </div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('தொடர்பு பகுப்பாய்வு', 'Comms Analytics')}</div>
          <div className="h-sub">{from_date} → {to_date}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={from_date} onChange={(e) => set_from_date(e.target.value)} max={to_date} style={{ border: '1px solid var(--line)', borderRadius: 4, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit' }} />
          <span style={{ color: 'var(--ink-3)' }}>→</span>
          <input type="date" value={to_date} onChange={(e) => set_to_date(e.target.value)} min={from_date} max={today} style={{ border: '1px solid var(--line)', borderRadius: 4, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" className="btn sm" onClick={() => { set_from_date(offset_ymd(today, -7)); set_to_date(today); }}>{t('7 நாள்', '7d')}</button>
            <button type="button" className="btn sm" onClick={() => { set_from_date(offset_ymd(today, -30)); set_to_date(today); }}>{t('30 நாள்', '30d')}</button>
            <button type="button" className="btn sm" onClick={() => { set_from_date(offset_ymd(today, -90)); set_to_date(today); }}>{t('90 நாள்', '90d')}</button>
          </div>
          <button type="button" className="btn" onClick={export_csv} disabled={!data}>
            <Icon.download /> CSV
          </button>
          <button type="button" className="btn primary" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : 'PDF'}
          </button>
        </div>
      </div>

      {q.isLoading && <div style={{ padding: 20, color: 'var(--ink-3)' }}>{t('ஏற்றுகிறது…', 'Loading…')}</div>}
      {q.error && <div style={{ padding: 20, color: 'var(--bad)' }}>{friendly_error(q.error)}</div>}

      {data && (
        <>
          <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {channel_card('வாட்ஸ்அப்', 'WhatsApp', data.totals.whatsapp, 'primary')}
            {channel_card('மின்னஞ்சல்', 'Email', data.totals.email)}
            {channel_card('குரல்', 'Voice', data.totals.voice)}
          </div>

          {data.top_intents.length > 0 && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-head"><div className="card-title">{t('முதன்மை வகைகள்', 'Top message types')}</div></div>
              <div style={{ padding: 14 }}>
                {data.top_intents.map((i, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, borderBottom: idx < data.top_intents.length - 1 ? '1px solid var(--line)' : undefined }}>
                    <span style={{ width: 20, color: 'var(--ink-3)' }}>{idx + 1}.</span>
                    <span style={{ flex: 1 }}>{i.kind}</span>
                    <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase' }}>{i.channel}</span>
                    <span className="mono" style={{ minWidth: 40, textAlign: 'right' }}>{i.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-head"><div className="card-title">{t('நாள் வாரியாக', 'Daily activity')}</div></div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ background: 'var(--bg-elev)' }}>
                  <tr>
                    <th rowSpan={2} style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('தேதி', 'Date')}</th>
                    <th colSpan={3} style={{ textAlign: 'center', padding: '4px 10px', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>{t('வாட்ஸ்அப்', 'WhatsApp')}</th>
                    <th colSpan={2} style={{ textAlign: 'center', padding: '4px 10px', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>{t('மின்னஞ்சல்', 'Email')}</th>
                    <th colSpan={2} style={{ textAlign: 'center', padding: '4px 10px', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>{t('குரல்', 'Voice')}</th>
                  </tr>
                  <tr style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500 }}>{t('அனுப்', 'Sent')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500 }}>{t('பெறப்', 'Delivered')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500 }}>{t('படிக்', 'Read')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500 }}>{t('அனுப்', 'Sent')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500 }}>{t('திற', 'Opened')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500 }}>{t('அழை', 'Placed')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500 }}>{t('பெற', 'Answered')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((d) => {
                    const empty = d.whatsapp_sent + d.email_sent + d.voice_placed === 0;
                    return (
                      <tr key={d.date} style={{ borderTop: '1px solid var(--line)', opacity: empty ? 0.4 : 1 }}>
                        <td style={{ padding: '6px 10px' }} className="mono">{d.date}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{d.whatsapp_sent || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{d.whatsapp_delivered || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{d.whatsapp_read || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{d.email_sent || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{d.email_opened || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{d.voice_placed || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{d.voice_answered || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {pdf_m.error && <div style={{ color: 'var(--bad)', padding: 10 }}>{friendly_error(pdf_m.error)}</div>}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,20,0.45)', zIndex: 10000, padding: 16, overflow: 'auto' }}>
          <div style={{ maxWidth: 1100, margin: 'auto', background: 'white', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>PDF preview (DRY_RUN)</div>
              <button type="button" className="btn sm" onClick={() => set_preview(null)}>Close</button>
            </div>
            <iframe title="comms-preview" srcDoc={preview} style={{ width: '100%', height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
